import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-semibold tracking-tight ring-offset-background transition-[transform,box-shadow,background-color,color,border-color,filter] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:shadow-md hover:brightness-[0.94]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:shadow-md hover:brightness-[0.96]",
        outline:
          "border border-border/80 bg-card hover:bg-accent hover:text-accent-foreground hover:border-border hover:shadow-sm",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-muted hover:text-foreground",
        ghost:
          "rounded-2xl hover:bg-accent hover:text-accent-foreground",
        link: "font-medium text-foreground underline-offset-4 hover:underline hover:text-foreground/80 rounded-none active:scale-100",
      },
      size: {
        default: "min-h-[44px] px-6 py-2.5",
        sm: "min-h-[40px] h-9 px-4 text-sm",
        lg: "min-h-[48px] h-12 px-8 text-base",
        xl: "min-h-[52px] h-14 px-10 text-base",
        icon: "h-11 w-11 min-h-[44px] min-w-[44px] rounded-2xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }

